import React, {useState} from "react"
import PropTypes from "prop-types"
import {RouteComponentProps} from "react-router-dom"

import DealByUrlSlug from "@irati/deals/apollo/query/DealByUrlSlug"
import {shouldDisplayDealAvatar} from "@irati-web/perks/src/utils/deals/imageUtils"
import {determineDealThumbnailImage} from "@irati/deals/helpers/deals/appearance"
import withBranding, {WithBranding} from "@irati/brand/withBranding"
import {Row, Card, Col} from "@irati/ui/src/Layout"
import Spacing from "@irati/ui/src/Spacing"
import history from "@irati/utils/history"
import {Deal, Checkout, PaymentProvider} from "@irati/types"
import {PerksPaymentSelectOnChange, PerksPaymentSelectOption} from "@shared/payments/types"
import withAuth, {WithAuth} from "@irati/auth/helpers/auth/withAuth"
import Loading from "@irati/ui/src/Loading"
import Loader from "@irati/ui/src/Loading/Loader"
import withPerkOrReward, {WithPerkOrReward} from "@irati-web/flexi-rewards/src/utils/withPerkOrReward"
import tracking from "@irati-tracking/perks/perks"
import checkoutService from "@irati/checkout/services/checkout"
import redemptionFlow from "@irati/checkout/utils/redemption"
import detailsToWebAddress from "@irati/checkout/utils/redemption/helpers/detailsToWebAddress"
import {useIsCheckoutDotcomEnabledQuery} from "@irati/payments/hooks"
import getDealCurrency from "@irati/deals/helpers/deals/currency"

import Container from "../../components/ui/RedemptionFlowContainer"
import MobileFlushCol from "../../components/ui/MobileFlushCol"
import Continue from "../../components/DealSelectProducts/Continue"
import DealAvatar from "../../components/DealSelectProducts/DealLogo"
import RequireDealStatusOn from "../../components/RequireDealStatusOn"
import PaymentSelect from "../../components/DealPayment/PaymentSelect"
import PaymentForm from "../../components/DealPayment/PaymentForms"
import TermsAndConditions from "../../components/DealPayment/TermsAndConditions"
import OrderSummary from "../../components/OrderSummary/OrderSummary"
import DealBanner from "../../components/DealBanner"
import CheckoutStep from "../../components/CheckoutStep"
import CheckoutFlexiRedemptions from "../../components/CheckoutFlexiRedemptions"

export type OnPaymentSubmit = () => Promise<any>

enum Step {
  PaymentSelection,
  Payment,
  PaymentComplete
}

interface Props extends RouteComponentProps<{urlSlug: string}>, WithBranding, WithAuth, WithPerkOrReward {}

const Payment: React.FC<Props> = (props, context) => {
  const [step, setStep] = useState(Step.PaymentSelection)
  const [termsAgreed, setTermsAgreed] = useState(false)
  const [paymentBreakdown, setPaymentBreakdown] = useState<PerksPaymentSelectOption[]>([])
  const [checkout, setCheckout] = useState<Checkout | null>(null)
  const [load, setLoad] = useState<Promise<any>>(Promise.resolve())
  const {data: isCheckoutDotComEnabled, load: checkoutEnabledLoad} = useIsCheckoutDotcomEnabledQuery(null, {
    fetchPolicy: "cache-and-network"
  })

  const urlSlug = props.match.params.urlSlug

  const toggleTermsAndConditions = () => {
    setTermsAgreed(!termsAgreed)
  }

  const handlePaymentChange: PerksPaymentSelectOnChange = (providers) => {
    setPaymentBreakdown(providers)
  }

  const handlePaymentSelected = (deal: Deal) => {
    return async () => {
      const userId = props.auth.user.uuid
      const request = checkoutService.startCheckout(userId)
      tracking.redeemPerkStartCheckout(deal)
      setStep(Step.Payment)
      setLoad(request)
      const c = await request
      setCheckout(c)
    }
  }

  const trackCompleteEvent = (deal: Deal) => {
    if (checkout) {
      const {basket} = checkout
      const walletAmount = paymentBreakdown.find((t) => t.type === PaymentProvider.Wallet)?.amount || 0
      tracking.redeemPerkCompleteCheckout(deal, basket.total!.amount!, basket.total!.currency!, walletAmount)
    }
  }

  const handleComplete = (deal: Deal) => async () => {
    if (checkout) {
      const request = redemptionFlow.paymentComplete(deal, checkout.uuid, checkout.paymentOrder.uuid)
      setLoad(request)
      // Changing step state prevents PaymentForm to re-render and attempt to create a new PaymentOrder
      setStep(Step.PaymentComplete)
      trackCompleteEvent(deal)
      const details = await request
      const url = detailsToWebAddress(details)
      history.replace(url, context)
    }
  }

  const isWalletSelected = () => {
    return !!paymentBreakdown.find((breakdown) => breakdown.type === PaymentProvider.Wallet)
  }

  return (
    <DealByUrlSlug variables={{urlSlug, isRewards: props.isRewards}}>
      {({data}) => {
        if (!data || !data.dealBySlug) return <RequireDealStatusOn deal={null} />
        const deal = data.dealBySlug

        const dealThumbnailSrc = shouldDisplayDealAvatar(deal) ? determineDealThumbnailImage(deal, props.branding) : null

        return (
          <>
            <DealBanner deal={deal} showFavouriteIcon={false} />
            <Container>
              <Row>
                <MobileFlushCol xs={12} md={8}>
                  <Card marginBottom={4}>
                    <Row>
                      <Col xs={12}>
                        <Loading load={load}>
                          {step === Step.PaymentSelection && (
                            <Loading load={checkoutEnabledLoad}>
                              {isCheckoutDotComEnabled ? (
                                <>
                                  {!redemptionFlow.isFlexiRedemption(getDealCurrency(deal)) ? (
                                    <CheckoutStep
                                      deal={deal}
                                      updatePaymentBreakdown={handlePaymentChange}
                                    />
                                  ) : (
                                    <CheckoutFlexiRedemptions deal={deal} paymentBreakdown={paymentBreakdown} />
                                  )}
                                </>
                              ) : (
                                <>
                                  <PaymentSelect deal={deal} onPaymentChange={handlePaymentChange} />
                                  <TermsAndConditions onChange={toggleTermsAndConditions} termsAgreed={termsAgreed} />
                                </>
                              )}
                            </Loading>
                          )}
                          {step === Step.Payment && checkout && (
                            <PaymentForm
                              deal={deal}
                              paymentBreakdown={paymentBreakdown}
                              checkout={checkout}
                              onComplete={handleComplete(deal)}
                            />
                          )}
                          {step === Step.PaymentComplete && <Loader />}
                        </Loading>
                      </Col>
                    </Row>
                  </Card>
                </MobileFlushCol>
                <MobileFlushCol xs={12} md={4}>
                  <Card>
                    <Row>
                      {dealThumbnailSrc && (
                        <Col xs={12}>
                          <DealAvatar src={dealThumbnailSrc} />
                        </Col>
                      )}
                      <Col xs={12}>
                        <Spacing marginBottom={4}>
                          <OrderSummary
                            deal={deal}
                            walletBalanceToUse={paymentBreakdown.find((t) => t.type === PaymentProvider.Wallet)?.amount}
                          />
                        </Spacing>
                        <Loading load={checkoutEnabledLoad} useLoader={false}>
                          {step === Step.PaymentSelection && !isCheckoutDotComEnabled && (
                            <Continue onClick={handlePaymentSelected(deal)} disabled={!termsAgreed} isPay={isWalletSelected()} />
                          )}
                        </Loading>
                      </Col>
                    </Row>
                  </Card>
                </MobileFlushCol>
              </Row>
            </Container>
          </>
        )
      }}
    </DealByUrlSlug>
  )
}

Payment.contextTypes = {
  appPath: PropTypes.string
}

export default withPerkOrReward(withBranding(withAuth(Payment)))
